import { Component, inject, input, signal, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import {
  ConfirmationModalComponent,
  DataTableComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormInputComponent,
} from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { Language, PageMode } from "../../../shared/constants/enums";
import { TableButtonClicked, ValidatorModelType } from "../../../shared/models";
import { LibTexMapValue } from "../../../shared/models/text-mapping-model";
import { AppService, SystemLayoutService, ValidatorApiService } from "../../../shared/services";
import { TextMappingsApiService } from "../../../shared/services/text-mapping-api-service";
import { TextMappingValueEditorComponent } from "../text-mapping-value-editor/text-mapping-value-editor";

@Component({
  selector: "adaa-text-mapping-editor",
  imports: [
    ReactiveFormsModule,
    RouterModule,
    TranslatePipe,
    FormActionButtonsComponent,
    FormCheckboxComponent,
    FormInputComponent,
    DataTableComponent,
    TextMappingValueEditorComponent,
  ],
  templateUrl: "./text-mapping-editor.html",
  styleUrl: "./text-mapping-editor.scss",
})
export class TextMappingEditorComponent {
  valueEditor = viewChild.required<TextMappingValueEditorComponent>("editor");
  adaaDataTable = viewChild.required<DataTableComponent>("adaaDataTable");

  private readonly _formBuilder = inject(FormBuilder);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _router = inject(Router);
  private readonly _modalService = inject(NgbModal);
  private readonly _appService = inject(AppService);
  private readonly _textMappingsApiService = inject(TextMappingsApiService);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _validatorApiService = inject(ValidatorApiService);

  textMappingForm: FormGroup = new FormGroup({});
  id = input<string>();
  pageMode = input.required<string>();
  valuePageMode = signal<string>(PageMode.view);

  Constants = Constants;
  PageMode = PageMode;

  submitted = signal<boolean>(false);
  texMapValues = signal<object[]>([]);
  texMapDeletedValues: LibTexMapValue[] = [];
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  readonly #untilDestroy = AdaaHelper.untilDestroyed();

  public get displayLabel(): string {
    const nameEN = this.textMappingForm.get("nameEN")?.value;
    const nameAE = this.textMappingForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this._prepareForm();

    const id = this.id();
    if (id) this._getTexyMapping(+id);
    this._refreshPage();
  }

  private _refreshPage() {
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this.#untilDestroy()).subscribe({
      next: () => {
        this._router.navigateByUrl("/console");
      },
    });
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.textMappingForm = this._formBuilder.group({
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      available: { value: null, disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_TEXT_MAPPINGS, this.textMappingForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.textMappingForm = response.form;
        },
      });
  }

  private _getTexyMapping(textMappingId: number) {
    this._textMappingsApiService.getTextMappingById(textMappingId).subscribe({
      next: (response) => {
        this.textMappingForm.patchValue({ ...response.responseData });
        this.texMapValues.set((response.responseData.libTexMapValues ?? []) as object[]);
        this.textMappingForm.markAsPristine();
        this.textMappingForm.markAsUntouched();
      },
    });
  }

  public checkInvalid(control: string) {
    return (
      this.textMappingForm.controls[control].errors?.["required"] &&
      (this.textMappingForm.get(`${control}`)?.dirty || this.submitted())
    );
  }

  public updateTextMapping() {
    this.submitted.set(true);
    if (this.textMappingForm.invalid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }
    const textMapping = this._formatOtherEntity();
    const serviceUrl =
      this.pageMode() === PageMode.edit
        ? this._textMappingsApiService.updateTextMapping(textMapping)
        : this._textMappingsApiService.createTextMapping(textMapping);

    serviceUrl.subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigateByUrl("/console/text-mapping");
      },
    });
  }

  private _formatOtherEntity() {
    const textMapping = this.textMappingForm.value;
    textMapping.libTexMapValues = [...this.texMapValues(), ...this.texMapDeletedValues];
    textMapping.status = Constants.OBJECT_STATUS.ACTIVE;
    if (this.pageMode() === PageMode.edit) {
      textMapping.updateTS = Date.now();
      const id = this.id();
      if (id) textMapping.id = +id;
    }
    return textMapping;
  }

  public cancelForm() {
    if (this.textMappingForm.dirty) {
      const modal = this._modalService.open(ConfirmationModalComponent, {
        centered: true,
        size: "md",
        modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
      });

      modal.componentInstance.header = "common.form.modals_leave.cancel_yes_no_title";
      modal.componentInstance.title = "common.form.modals_leave.cancel_yes_no_information";

      modal.result.then((e) => {
        if (e) this._router.navigateByUrl(`/console/text-mapping`);
      });
    } else this._router.navigateByUrl(`/console/text-mapping`);
  }

  public addNew() {
    this.valuePageMode.set(PageMode.create);
    this.valueEditor().textMappingValueForm.reset({});
    this.valueEditor().textMappingValueForm.enable();
    this.valueEditor().open();
  }

  public getAction(value: TableButtonClicked) {
    switch (value.event) {
      case "edit":
        this._handleTextMappingValueEditor(value, PageMode.edit);
        break;
      case "delete":
        this._deleteTextMappingValue(value);
        break;
      default:
        break;
    }
  }

  private _handleTextMappingValueEditor(value: TableButtonClicked, mode: string) {
    this.valuePageMode.set(mode);

    this.valueEditor().textMappingValueForm.patchValue(value.data);

    if (mode === PageMode.edit) {
      this.valueEditor().textMappingValueForm.patchValue({ id: value.data.id });
    }

    this.valueEditor().open();
  }

  private _deleteTextMappingValue(value: TableButtonClicked) {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "lg",
      centered: true,
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modal.componentInstance.header = "lov.value_delete_yes_no_title";
    modal.componentInstance.title = "lov.value_delete_yes_no_information";
    //change the status to be removed
    const dataToDelete = value.data;
    dataToDelete.status = Constants.OBJECT_STATUS.REMOVE;

    modal.result.then((e) => {
      if (e) {
        this.texMapValues.update((values) => {
          const indexToUpdate = values.findIndex((item) => (item as LibTexMapValue).id === value.data.id);
          values.splice(indexToUpdate, 1);
          //add it to deleted array if old value
          if (dataToDelete.id) this.texMapDeletedValues.push(dataToDelete);
          return [...values];
        });
        this.adaaDataTable().loadTableData();
      }
    });
  }

  public submit(data: LibTexMapValue) {
    if (this.valuePageMode() === PageMode.create) {
      this.texMapValues.update((values) => {
        return [...values, data];
      });
    } else {
      this.texMapValues.update((values) => {
        const indexToUpdate = values.findIndex((item) => (item as LibTexMapValue).id === data.id);
        values[indexToUpdate] = data;
        return [...values];
      });
    }

    this._modalService.dismissAll();
    this.adaaDataTable().loadTableData();
  }
}
